import {
  Alert,
  Stack,
  Button,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { AlertColor, ButtonProps, SxProps, Theme } from '@mui/material';
import { ReactNode } from 'react';

const bannerListDefaultStyle: SxProps<Theme> = {
  alignItems: 'center',
  borderRadius: '20px',
  backgroundColor: (theme) => theme.palette.background.paper,
  borderColor: 'transparent',
  color: (theme) => theme.palette.text.primary,

  '& .MuiAlert-icon': {
    color: (theme) => theme.palette.primary.main,
    mr: 1,
  },

  '& .MuiAlert-message': {
    padding: 0,
    fontSize: '0.875rem',
  },

  '& p': {
    margin: 0,
    padding: 0,
  },
};
const actionButtonDefaultStyle: SxProps<Theme> = {
  margin: 0,
  textTransform: 'none',
  fontWeight: 500,
  padding: '0 16px 4px 0',
  minWidth: 'auto',
  color: (theme) => theme.palette.primary.main,
  backgroundColor: 'transparent',
  textUnderlineOffset: '2px',

  '&:hover': {
    backgroundColor: 'transparent',
    textDecoration: 'none',
  },

  '&:focus-visible': {
    outline: (theme) => `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '2px',
  },
};

export interface BannerItem {
  id: string | number;
  content: ReactNode;
  severity?: AlertColor;
  icon?: ReactNode;
  actionLabel?: string;
  onDismiss?: () => void | Promise<void>;
  isActionDisabled?: boolean;
}

interface BannerListProps {
  items: BannerItem[];
  dismissLabel?: string;
  containerSx?: SxProps<Theme>;
  alertSx?: SxProps<Theme>;
  actionButtonSx?: SxProps<Theme>;
  actionButtonProps?: ButtonProps;
}

const BannerList = ({
  items,
  dismissLabel,
  containerSx,
  alertSx,
  actionButtonSx,
  actionButtonProps,
}: BannerListProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (items.length === 0) {
    return null;
  }

  return (
    <Stack
      spacing={2}
      sx={containerSx}
    >
      {items.map((item) => {
        const label = item.actionLabel ?? dismissLabel;
        const action =
          item.onDismiss && label ? (
            <Button
              {...actionButtonProps}
              onClick={() => {
                void item.onDismiss?.();
              }}
              disabled={item.isActionDisabled}
              sx={{
                ...actionButtonDefaultStyle,
                ...(actionButtonSx as object),
              }}
            >
              {label}
            </Button>
          ) : undefined;

        if (isMobile) {
          return (
            <Box
              key={item.id}
              sx={{
                ...bannerListDefaultStyle,
                ...(alertSx as object),
                display: 'flex',
                flexDirection: 'column',
                // Override the desktop Alert's alignItems:'center' (from the shared
                // styles) so the message goes full-width/left-aligned, not centered.
                alignItems: 'stretch',
                gap: 1,
                padding: 2,
              }}
            >
              {/* Message (icon + text) on top, action stacked below — matches the
                  legacy site and never cramps long messages. */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                {item.icon}
                <Box sx={{ flex: 1, minWidth: 0 }}>{item.content}</Box>
              </Box>
              {action && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  {action}
                </Box>
              )}
            </Box>
          );
        }

        // Desktop layout: Keep current Alert component
        return (
          <Alert
            key={item.id}
            icon={item.icon}
            variant='outlined'
            severity={item.severity ?? 'info'}
            action={action}
            sx={{ ...bannerListDefaultStyle, ...(alertSx as object) }}
          >
            {item.content}
          </Alert>
        );
      })}
    </Stack>
  );
};

export default BannerList;
